// State for the "External link" create-modal, lifted out of the per-row
// CreateNode component so the Modal renders at the SpaceTree top level —
// outside the react-arborist <Tree> subtree.
//
// Inside a tree row, react-arborist's row focus + keyboard handlers fight
// the Modal's text inputs for focus on every keystroke, making the inputs
// effectively uneditable. Hoisting the Modal out fixes that completely.
//
// Same dual-instance-of-jotai caveat as pending-create-atom.ts: we use a
// plain module-level state with a subscribe/notify pattern rather than a
// jotai atom, to guarantee writes from a row component are seen by a
// reader subscribed at the SpaceTree root.

import { useEffect, useState } from "react";

export type ExternalLinkModalRequest = {
  parentNodeId: string;
  // Forwarded to the tree-row create flow once the user submits the modal.
  // CreateNode passes this so the SpaceTree-level handler can re-run the
  // same `tree.create({ type: "internal", parentId })` call.
  onSubmit: (payload: { title: string; url: string }) => void;
};

let current: ExternalLinkModalRequest | null = null;
const listeners = new Set<(v: ExternalLinkModalRequest | null) => void>();

export function openExternalLinkModal(req: ExternalLinkModalRequest): void {
  current = req;
  for (const l of listeners) l(current);
}

export function closeExternalLinkModal(): void {
  current = null;
  for (const l of listeners) l(null);
}

export function useExternalLinkModal(): ExternalLinkModalRequest | null {
  const [value, setValue] = useState<ExternalLinkModalRequest | null>(current);
  useEffect(() => {
    const cb = (v: ExternalLinkModalRequest | null) => setValue(v);
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  }, []);
  return value;
}
